import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { LogoMark } from "./Logo";
import { Footer } from "./Footer";

// Renders one content block from a section.
// Verbatim text lives inside `text` / `items` / `rows` — never rewritten here.
const Block = ({ block }) => {
  switch (block.type) {
    case "p":
      return (
        <p className="font-body text-[16px] leading-[1.8] text-[#4b463e]">
          {block.text}
        </p>
      );
    case "ul":
      return (
        <ul className="space-y-2">
          {block.items.map((it, i) => (
            <li key={i} className="flex gap-3 font-body text-[16px] leading-[1.75] text-[#4b463e]">
              <span className="mt-[10px] inline-block h-[5px] w-[5px] flex-none rounded-full bg-[#EA6A43]" />
              <span>{it}</span>
            </li>
          ))}
        </ul>
      );
    case "sub":
      // Subsection heading like "1.1 Purpose" — semantically an H3 but styled quietly
      return (
        <h3 className="pt-3 font-display text-[18px] font-semibold tracking-tight text-[#1b1a18] sm:text-[20px]">
          <span className="mr-2 text-[#EA6A43]">{block.number}</span>
          {block.title}
        </h3>
      );
    case "label":
      // A small standalone strong label line (e.g. "Target Initial Response")
      return (
        <p className="font-body text-[13px] font-semibold uppercase tracking-[0.14em] text-[#9a958c]">
          {block.text}
        </p>
      );
    case "table":
      return (
        <div className="overflow-x-auto">
          <table className="mt-2 w-full border-collapse text-left font-body text-[15px]">
            <tbody>
              {block.rows.map((row, i) => (
                <tr
                  key={i}
                  className={i === 0 ? "border-y border-[var(--mo-line)] bg-[#efeadd]" : "border-b border-[var(--mo-line)]"}
                >
                  {row.map((cell, j) => (
                    <td
                      key={j}
                      className={`px-4 py-3 align-top ${
                        i === 0
                          ? "font-semibold text-[#1b1a18]"
                          : j === 0
                          ? "font-semibold text-[#1b1a18]"
                          : "text-[#4b463e]"
                      }`}
                    >
                      {cell}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    default:
      return null;
  }
};

// A section = a numbered heading + body blocks. The anchor id lets the TOC jump to it.
const Section = ({ section }) => (
  <section id={section.id} className="scroll-mt-24">
    <h2 className="font-display text-[26px] font-black leading-[1.15] tracking-tight text-[#1b1a18] sm:text-[30px]">
      <span className="mr-3 text-[#EA6A43]">{section.number}</span>
      {section.title}
    </h2>
    <div className="mt-5 space-y-5">
      {section.body.map((b, i) => (
        <Block key={i} block={b} />
      ))}
    </div>
  </section>
);

// Sticky table of contents used on lg+. Highlights the section currently in view.
const TableOfContents = ({ sections }) => {
  const [active, setActive] = useState(sections[0]?.id);

  useEffect(() => {
    const io = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible[0]) setActive(visible[0].target.id);
      },
      { rootMargin: "-20% 0px -70% 0px", threshold: 0 }
    );
    sections.forEach((s) => {
      const el = document.getElementById(s.id);
      if (el) io.observe(el);
    });
    return () => io.disconnect();
  }, [sections]);

  return (
    <nav aria-label="Table of contents" className="sticky top-24 hidden lg:block">
      <div className="font-body text-[11px] font-semibold uppercase tracking-[0.24em] text-[#9a958c]">
        On this page
      </div>
      <ol className="mt-4 space-y-2 border-l border-[var(--mo-line)] pl-4">
        {sections.map((s) => (
          <li key={s.id}>
            <a
              href={`#${s.id}`}
              className={`block font-body text-[13px] leading-snug transition-colors ${
                active === s.id
                  ? "text-[#1b1a18]"
                  : "text-[#6b665e] hover:text-[#1b1a18]"
              }`}
            >
              <span className="mr-1.5 text-[#EA6A43]">{s.number}</span>
              {s.title}
            </a>
          </li>
        ))}
      </ol>
    </nav>
  );
};

// Full page shell: header + eyebrow + title + date pills + TOC + sections + Footer.
export const LegalPage = ({ eyebrow, title, effectiveDate, lastUpdated, sections, testId }) => {
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = `${title} — MiseOne`;
    return () => {
      document.title = "MiseOne — The Digital Mise en Place";
    };
  }, [title]);

  const hasDates = useMemo(() => effectiveDate || lastUpdated, [effectiveDate, lastUpdated]);

  return (
    <div className="relative min-h-screen overflow-x-clip bg-[#f5f2ea]">
      <header className="mx-auto flex w-full max-w-[1600px] items-center justify-between px-6 py-6 md:px-12">
        <Link to="/" className="inline-flex items-center gap-3" data-testid="legal-shell-home">
          <LogoMark size={30} />
          <span className="font-editorial text-2xl tracking-tight text-[#1b1a18]">Mise One</span>
        </Link>
        <Link
          to="/"
          className="inline-flex items-center gap-2 rounded-full border border-[var(--mo-line)] bg-white/60 px-5 py-2 font-body text-[13px] font-medium text-[#1b1a18] transition-colors hover:bg-white"
          data-testid="legal-shell-back"
        >
          <ArrowLeft size={14} strokeWidth={1.8} />
          Back to site
        </Link>
      </header>

      <main
        data-testid={testId}
        className="mx-auto w-full max-w-[1400px] px-6 pt-8 pb-24 md:px-12 md:pt-14 md:pb-32"
      >
        {/* Title block */}
        <div className="max-w-3xl">
          <span className="font-body text-[11px] uppercase tracking-[0.32em] text-[#EA6A43]">
            {eyebrow}
          </span>
          <h1 className="mt-5 font-display text-5xl font-black leading-[0.98] tracking-tighter text-[#1b1a18] sm:text-6xl">
            {title}
          </h1>

          {hasDates && (
            <div className="mt-6 flex flex-wrap gap-2">
              {effectiveDate && (
                <span className="rounded-full border border-[var(--mo-line)] bg-white/60 px-3 py-1 font-body text-[12px] text-[#4b463e]">
                  <span className="font-semibold text-[#1b1a18]">Effective Date:</span> {effectiveDate}
                </span>
              )}
              {lastUpdated && (
                <span className="rounded-full border border-[var(--mo-line)] bg-white/60 px-3 py-1 font-body text-[12px] text-[#4b463e]">
                  <span className="font-semibold text-[#1b1a18]">Last Updated:</span> {lastUpdated}
                </span>
              )}
            </div>
          )}
        </div>

        {/* Two-column layout: TOC + content */}
        <div className="mt-14 grid grid-cols-1 gap-x-16 gap-y-10 lg:grid-cols-[240px_minmax(0,1fr)] lg:gap-x-20">
          <TableOfContents sections={sections} />
          <div className="max-w-3xl space-y-14">
            {sections.map((s) => (
              <Section key={s.id} section={s} />
            ))}
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default LegalPage;
